import { useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { formatEUR } from "@/lib/format";
import { FadeUp } from "@/components/Motion";
import BeeTileBackdrop from "@/components/BeeTileBackdrop";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { Gift, ArrowUpRight, Sparkles } from "lucide-react";

const COPY = {
  de: {
    eyebrow: "GESCHENK",
    title: ["Ein Geschenk", "aus den Dolomiten."],
    lead: "Verschenke Madubau — puren Alpenhonig, gereift auf 1.100 Metern. Der Beschenkte bekommt einen persönlichen Code per E-Mail, der beim nächsten Einkauf angerechnet wird.",
    amount: "Wunschbetrag",
    minHelp: "Mindestbetrag € 25 · frei wählbar",
    quickAmounts: "Beliebte Werte",
    section_purchaser: "Von",
    section_recipient: "An",
    section_message: "Persönliche Nachricht",
    p_name: "Dein Name",
    p_email: "Deine E-Mail (Bestätigung)",
    r_name: "Name des Beschenkten",
    r_email: "E-Mail des Beschenkten (leer lassen = Zustellung an dich)",
    message: "Nachricht auf der Gutschein-Karte (optional)",
    submit: "Gutschein kaufen",
    self_note: "Wenn du keine Empfänger-E-Mail angibst, senden wir den Gutschein-Code an deine E-Mail — perfekt zum Ausdrucken.",
    trust: ["Sichere Zahlung via Stripe", "Unmittelbare Zustellung", "Übertragbar, gültig bis vollständig eingelöst"],
  },
  en: {
    eyebrow: "GIFT",
    title: ["A gift", "from the Dolomites."],
    lead: "Give Madubau — pure alpine honey, aged at 1,100 metres. Your recipient gets a personal code by email that is redeemed at checkout.",
    amount: "Amount",
    minHelp: "Minimum € 25 · any amount",
    quickAmounts: "Popular amounts",
    section_purchaser: "From",
    section_recipient: "To",
    section_message: "Personal message",
    p_name: "Your name",
    p_email: "Your email (confirmation)",
    r_name: "Recipient name",
    r_email: "Recipient email (leave blank = deliver to you)",
    message: "Message on the card (optional)",
    submit: "Buy gift card",
    self_note: "If you don't enter a recipient email, we'll send the gift code to you — perfect for printing.",
    trust: ["Secure payment via Stripe", "Instant delivery", "Transferable, valid until fully redeemed"],
  },
  it: {
    eyebrow: "REGALO",
    title: ["Un regalo", "dalle Dolomiti."],
    lead: "Regala Madubau — puro miele alpino, maturato a 1.100 metri. Il destinatario riceve un codice personale via email da spendere al prossimo acquisto.",
    amount: "Importo",
    minHelp: "Minimo € 25 · a scelta",
    quickAmounts: "Importi popolari",
    section_purchaser: "Da",
    section_recipient: "A",
    section_message: "Messaggio personale",
    p_name: "Il tuo nome",
    p_email: "La tua email (conferma)",
    r_name: "Nome del destinatario",
    r_email: "Email del destinatario (vuoto = invia a te)",
    message: "Messaggio sulla carta (opzionale)",
    submit: "Acquista buono regalo",
    self_note: "Se non inserisci un'email destinatario, invieremo il codice a te — perfetto da stampare.",
    trust: ["Pagamento sicuro tramite Stripe", "Consegna immediata", "Trasferibile, valido fino a esaurimento"],
  },
  ja: {
    eyebrow: "ギフト",
    title: ["ドロミテからの", "贈り物。"],
    lead: "Madubauを贈りませんか——標高1,100mで熟成した純粋なアルプスのはちみつ。受け取る方には個人コードがメールで届き、次回のお買い物でご利用いただけます。",
    amount: "金額",
    minHelp: "最低 € 25 · 自由に設定",
    quickAmounts: "人気の金額",
    section_purchaser: "贈る方",
    section_recipient: "贈られる方",
    section_message: "パーソナルメッセージ",
    p_name: "お名前",
    p_email: "メールアドレス(確認用)",
    r_name: "贈られる方のお名前",
    r_email: "贈られる方のメール(空欄=ご自身に届きます)",
    message: "カードに添えるメッセージ(任意)",
    submit: "ギフトカードを購入",
    self_note: "受取人のメールを入力しない場合、コードはご自身のメールに届きます——印刷にも最適です。",
    trust: ["安全な決済", "即時お届け", "譲渡可能・使い切るまで有効"],
  },
  zh: {
    eyebrow: "赠礼",
    title: ["来自多洛米蒂的", "礼物。"],
    lead: "送出 Madubau——在海拔1,100米熟成的纯净阿尔卑斯蜂蜜。收礼人将收到专属兑换码邮件,可在下次购物时抵扣使用。",
    amount: "金额",
    minHelp: "最低 € 25 · 任意金额",
    quickAmounts: "热门金额",
    section_purchaser: "赠送人",
    section_recipient: "收礼人",
    section_message: "个人留言",
    p_name: "您的姓名",
    p_email: "您的邮箱(用于确认)",
    r_name: "收礼人姓名",
    r_email: "收礼人邮箱(留空则发送给您)",
    message: "礼品卡留言(可选)",
    submit: "购买礼品卡",
    self_note: "如不填写收礼人邮箱,兑换码将发送到您的邮箱——方便打印赠送。",
    trust: ["安全支付", "即时送达", "可转赠,用完即止"],
  },
};

const QUICK = [25, 50, 100, 150];

export default function GiftCard() {
  const { t, lang } = useI18n();
  const L = COPY[lang] || COPY.de;
  const [amount, setAmount] = useState(50);
  const [pName, setPName] = useState("");
  const [pEmail, setPEmail] = useState("");
  const [rName, setRName] = useState("");
  const [rEmail, setREmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (amount < 25) { toast.error("min. € 25"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/gift-card/purchase", {
        amount_eur: Number(amount),
        origin_url: window.location.origin,
        purchaser_email: pEmail,
        purchaser_name: pName,
        recipient_email: rEmail || undefined,
        recipient_name: rName,
        personal_message: message,
        language: lang,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error("Fehler beim Kauf");
      setBusy(false);
    }
  };

  return (
    <div className="pt-32 pb-24 min-h-screen bg-black relative overflow-x-hidden" data-testid="gift-card-page">
      <Seo title={`${L.title.join(" ")} — Madubau Dolomiti`} description={t.seo.giftcard_d} path="/gift-card" />
      <BeeTileBackdrop opacity={0.22} />
      <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
        {/* LEFT — Story panel */}
        <FadeUp className="lg:col-span-5 lg:sticky lg:top-32 self-start min-w-0">
          <div className="font-display text-[10px] tracking-[0.45em] text-gold mb-6">/ {L.eyebrow}</div>
          <h1
            className="font-editorial italic font-light text-gold leading-[0.95] mb-10 break-words"
            style={{ fontSize: "clamp(2rem, 7vw, 5rem)" }}
          >
            {L.title[0]}
            <br />
            {L.title[1]}
          </h1>
          <p className="text-cream/70 font-body text-base leading-[1.85] mb-10 max-w-full sm:max-w-md">
            {L.lead}
          </p>

          {/* Mock preview card */}
          <div className="relative border border-gold/40 bg-[#0d0d0d] p-6 sm:p-8 max-w-full sm:max-w-md">
            <div className="font-display text-[9px] tracking-[0.4em] text-gold mb-4">— MADUBAU DOLOMITI —</div>
            <div className="font-editorial italic text-2xl text-cream mb-6">Für dich</div>
            <div className="border border-gold/30 bg-black/60 p-5 mb-6">
              <div className="font-display text-[9px] tracking-[0.35em] text-gold/70 mb-1">GUTSCHEINWERT</div>
              <div className="font-editorial text-3xl sm:text-4xl text-gold break-words">{formatEUR(amount, lang)}</div>
            </div>
            <div className="flex items-center gap-2 text-cream/50 text-xs font-body">
              <Sparkles className="w-3.5 h-3.5 text-gold/80" />
              <span>MB-XXXX-XXXX-XXXX</span>
            </div>
          </div>

          <ul className="mt-10 space-y-2 text-xs font-body text-cream/50">
            {L.trust.map((line, i) => (
              <li key={i}>· {line}</li>
            ))}
          </ul>
        </FadeUp>

        {/* RIGHT — Form */}
        <FadeUp delay={0.15} className="lg:col-span-7 min-w-0">
          <form onSubmit={submit} className="space-y-10">
            {/* Amount */}
            <div>
              <label className="font-display text-[10px] tracking-[0.35em] text-cream/60 mb-3 block">{L.amount}</label>
              <div className="flex items-baseline gap-3 border border-gold/40 bg-[#0d0d0d] px-4 sm:px-6 py-5 min-w-0 overflow-hidden">
                <span className="font-editorial text-3xl text-gold shrink-0">€</span>
                <input
                  type="number"
                  min="25"
                  step="1"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  data-testid="gc-amount"
                  className="flex-1 min-w-0 w-full bg-transparent outline-none font-editorial text-3xl sm:text-4xl text-gold"
                />
              </div>
              <p className="text-[10px] text-cream/40 mt-2 font-body">{L.minHelp}</p>
              <div className="flex gap-2 mt-4">
                {QUICK.map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setAmount(v)}
                    data-testid={`gc-quick-${v}`}
                    className={`px-4 py-2 text-xs font-display tracking-[0.3em] transition-colors ${
                      Number(amount) === v ? "bg-gold text-black" : "border border-white/15 text-cream/70 hover:border-gold hover:text-gold"
                    }`}
                  >
                    € {v}
                  </button>
                ))}
              </div>
            </div>

            {/* From */}
            <div>
              <label className="font-display text-[10px] tracking-[0.35em] text-gold mb-3 block">/ {L.section_purchaser}</label>
              <div className="space-y-4">
                <input
                  type="text"
                  required
                  value={pName}
                  onChange={(e) => setPName(e.target.value)}
                  placeholder={L.p_name}
                  data-testid="gc-purchaser-name"
                  className="bg-transparent border-b border-white/15 focus:border-gold py-3 outline-none w-full text-cream font-body placeholder:text-cream/30"
                />
                <input
                  type="email"
                  required
                  value={pEmail}
                  onChange={(e) => setPEmail(e.target.value)}
                  placeholder={L.p_email}
                  data-testid="gc-purchaser-email"
                  className="bg-transparent border-b border-white/15 focus:border-gold py-3 outline-none w-full text-cream font-body placeholder:text-cream/30"
                />
              </div>
            </div>

            {/* To */}
            <div>
              <label className="font-display text-[10px] tracking-[0.35em] text-gold mb-3 block">/ {L.section_recipient}</label>
              <div className="space-y-4">
                <input
                  type="text"
                  value={rName}
                  onChange={(e) => setRName(e.target.value)}
                  placeholder={L.r_name}
                  data-testid="gc-recipient-name"
                  className="bg-transparent border-b border-white/15 focus:border-gold py-3 outline-none w-full text-cream font-body placeholder:text-cream/30"
                />
                <input
                  type="email"
                  value={rEmail}
                  onChange={(e) => setREmail(e.target.value)}
                  placeholder={L.r_email}
                  data-testid="gc-recipient-email"
                  className="bg-transparent border-b border-white/15 focus:border-gold py-3 outline-none w-full text-cream font-body placeholder:text-cream/30"
                />
                <p className="text-[10px] text-cream/40 font-body italic">{L.self_note}</p>
              </div>
            </div>

            {/* Message */}
            <div>
              <label className="font-display text-[10px] tracking-[0.35em] text-gold mb-3 block">/ {L.section_message}</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value.slice(0, 400))}
                placeholder={L.message}
                rows={4}
                data-testid="gc-message"
                className="bg-[#0d0d0d] border border-white/15 focus:border-gold py-3 px-4 outline-none w-full text-cream/85 font-body text-sm placeholder:text-cream/30 resize-none"
              />
              <p className="text-[10px] text-cream/40 mt-2 font-body">{message.length}/400</p>
            </div>

            <button
              type="submit"
              disabled={busy}
              data-testid="gc-submit"
              className="w-full py-5 bg-gold text-black font-display text-[11px] tracking-[0.35em] hover:bg-gold-light gold-shine inline-flex items-center justify-center gap-3"
              style={{ background: "#C9A24C" }}
            >
              <Gift className="w-4 h-4" />
              {L.submit} · {formatEUR(amount, lang)}
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </form>
        </FadeUp>
      </div>
    </div>
  );
}
